// Reads the user's subscription status from the Supabase `subscriptions`
// table and exposes it to the auth gate. Hour 9's Stripe webhook is what
// actually writes status='active' onto this row after Checkout completes;
// until then, every newly-signed-up user reads as 'inactive' and is held
// on the SubscribePage.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthContext';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used inside <SubscriptionProvider>.');
  }
  return ctx;
}

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  // Plan tier ('basic' | 'premium' | 'enterprise' | null). Drives the
  // feature-gate map (src/lib/plans.js). null means "we don't know
  // yet" — the UI defaults to the most-restricted tier in that case.
  const [plan, setPlan] = useState(null);
  // Start `true` so the auth gate shows a spinner instead of bouncing the
  // user to SubscribePage during the very first query.
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const refreshSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscriptionStatus('inactive');
      setPlan(null);
      setLoadingSubscription(false);
      return;
    }
    setLoadingSubscription(true);
    try {
      // Try WITH comp (grandfathered/complimentary accounts); fall back if the
      // column isn't migrated yet so the gate never breaks in the interim.
      let { data, error } = await supabase
        .from('subscriptions')
        .select('status, plan, comp')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) {
        ({ data, error } = await supabase
          .from('subscriptions')
          .select('status, plan')
          .eq('user_id', user.id)
          .maybeSingle());
      }
      if (error) {
        // If the table is missing or RLS blocks the read, fail closed:
        // treat as inactive so the user lands on SubscribePage rather
        // than silently slipping into the app.
        // eslint-disable-next-line no-console
        console.error('subscription query failed', error);
        setSubscriptionStatus('inactive');
        setPlan(null);
      } else {
        // A comp account gets full access — treat it as active everywhere.
        setSubscriptionStatus(data?.comp ? 'active' : (data?.status || 'inactive'));
        setPlan(data?.plan || null);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('subscription query threw', e);
      setSubscriptionStatus('inactive');
      setPlan(null);
    } finally {
      setLoadingSubscription(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const value = { subscriptionStatus, plan, loadingSubscription, refreshSubscription };
  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
